import {TFunction} from '@wix/yoshi-flow-editor';
import {Details, InvalidCouponReason, InvalidCouponStatusErrorData} from '@wix/auto_sdk_ecom_cart-v-2';
import {PriceModel} from './Price.model';
import {ModelContext} from '../../types/app.types';

export const AddCouponErrorCode: Record<string, string> = {
  INVALID_COUPON_CODE: 'INVALID_COUPON_CODE',
  INVALID_COUPON_STATUS: 'INVALID_COUPON_STATUS',
};

export class AddCouponErrorModel {
  public code: string;
  public message: string;

  constructor(params: AddCouponErrorModel) {
    this.code = params.code;
    this.message = params.message;
  }

  public static fromCartV1(error: any, t: TFunction): AddCouponErrorModel {
    const errorCode =
      error?.details?.applicationError?.code ??
      error?.validationError?.fieldViolations?.[0]?.ruleName ??
      'ERROR_COUPON_GENERAL_ERROR';

    const description = error?.details?.applicationError?.description;

    return new AddCouponErrorModel({
      code: errorCode,
      message: toMessageV1(errorCode, description, t),
    });
  }

  public static fromCartV2(
    httpError: any,
    {t, modelContext}: {t: TFunction; modelContext: ModelContext}
  ): AddCouponErrorModel {
    const error = (httpError?.response?.data as {details: Details})?.details;
    const applicationError = error?.applicationError;
    const validationError = error?.validationError;
    if (validationError) {
      return new AddCouponErrorModel({
        code:
          validationError.fieldViolations?.[0].violatedRule ?? /* istanbul ignore next */ 'ERROR_COUPON_GENERAL_ERROR',
        message: toMessageV2({
          code: 'ERROR_COUPON_GENERAL_ERROR',
          t,
          modelContext,
        }),
      });
    }
    const code = applicationError?.code ?? /* istanbul ignore next */ 'ERROR_COUPON_GENERAL_ERROR';
    return new AddCouponErrorModel({
      code,
      message: toMessageV2({code, data: applicationError?.data as InvalidCouponStatusErrorData, t, modelContext}),
    });
  }
}

function toMessageV1(code: string, description: string, t: TFunction): string {
  if (code === 'ERROR_INVALID_SUBTOTAL') {
    return t('cart.errors.coupons.ERROR_INVALID_SUBTOTAL', {subtotal: description});
  }
  if (code === 'ERROR_INVALID_PRODUCT_QUANTITY') {
    return t('cart.errors.coupons.ERROR_INVALID_PRODUCT_QUANTITY', {quantity: description});
  }
  return t(`cart.errors.coupons.${code}`);
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function toMessageV2({
  code,
  data,
  t,
  modelContext,
}: {
  code: string;
  data?: InvalidCouponStatusErrorData;
  t: TFunction;
  modelContext: ModelContext;
}): string {
  if (code === AddCouponErrorCode.INVALID_COUPON_CODE) {
    return t('cart.errors.coupons.ERROR_COUPON_DOES_NOT_EXIST');
  }
  if (code === AddCouponErrorCode.INVALID_COUPON_STATUS) {
    if (data?.reason === InvalidCouponReason.EXPIRED) {
      return t('cart.errors.coupons.ERROR_COUPON_HAS_EXPIRED');
    }
    if (data?.reason === InvalidCouponReason.DISABLED) {
      return t('cart.errors.coupons.ERROR_COUPON_IS_DISABLED');
    }
    if (data?.reason === InvalidCouponReason.INACTIVE) {
      return t('cart.errors.coupons.ERROR_COUPON_IS_NOT_ACTIVE_YET');
    }
    if (data?.reason === InvalidCouponReason.MAX_USAGE_EXCEEDED) {
      return t('cart.errors.coupons.ERROR_COUPON_USAGE_EXCEEDED');
    }
    if (data?.reason === InvalidCouponReason.MAX_USAGE_PER_CUSTOMER_EXCEEDED) {
      return t('cart.errors.coupons.ERROR_COUPON_LIMIT_PER_CUSTOMER_EXCEEDED');
    }
    if (data?.reason === InvalidCouponReason.NOT_APPLICABLE_TO_SUBSCRIPTIONS) {
      return t('cart.errors.coupons.ERROR_NOT_APPLICABLE_FOR_SUBSCRIPTIONS');
    }
    if (data?.reason === InvalidCouponReason.NOT_APPLICABLE_TO_SELECTED_LINE_ITEMS) {
      return t('cart.errors.coupons.ERROR_INVALID_PRODUCTS');
    }
    if (data?.reason === InvalidCouponReason.MIN_LINE_ITEM_QUANTITY_NOT_REACHED) {
      return t('cart.errors.coupons.ERROR_INVALID_PRODUCT_QUANTITY', {
        quantity: data?.minLineItemQuantityNotReachedDetails?.requiredMinimumQuantity,
      });
    }
    if (data?.reason === InvalidCouponReason.MIN_SUBTOTAL_NOT_REACHED) {
      const subtotal = PriceModel.fromCartV2(data?.minSubtotalNotReachedDetails?.requiredMinimumSubtotal, modelContext);
      return t('cart.errors.coupons.ERROR_INVALID_SUBTOTAL', {
        subtotal: subtotal.formattedConvertedAmount,
      });
    }
  }
  return t('cart.errors.coupons.ERROR_COUPON_DOES_NOT_EXIST');
}
